import React, { useEffect, useState } from "react";
import emailjs from "@emailjs/browser";

import Noise from "../../../components/animations/Noise.jsx";
import WavyText from "../../../components/animations/WavyText.jsx";

/* ================================================================
   EMAILJS CONFIGURATION
================================================================ */

const PUBLIC_KEY =
  process.env.REACT_APP_EMAILJS_ENQUIRY_PUBLIC_KEY;

const ENQUIRY_SERVICE_ID =
  process.env.REACT_APP_EMAILJS_ENQUIRY_SERVICE_ID;

const TEMPLATE_ID =
  process.env.REACT_APP_EMAILJS_ENQUIRY_TEMPLATE_ID;

/* Admin / enquiry recipient email */
const ENQUIRY_EMAIL =
  process.env.REACT_APP_ENQUIRY_EMAIL;

/* ================================================================
   SEND ENQUIRY
================================================================ */

const sendEnquiry = async (formData) => {
  try {
    /* ============================================================
       CONFIGURATION VALIDATION
    ============================================================ */

    if (!PUBLIC_KEY) {
      throw new Error(
        "EmailJS enquiry public key is missing."
      );
    }

    if (!ENQUIRY_SERVICE_ID) {
      throw new Error(
        "EmailJS enquiry service ID is missing."
      );
    }

    if (!TEMPLATE_ID) {
      throw new Error(
        "EmailJS enquiry template ID is missing."
      );
    }

    if (!ENQUIRY_EMAIL) {
      throw new Error(
        "REACT_APP_ENQUIRY_EMAIL is missing from .env"
      );
    }

    /* ============================================================
       FORM DATA
    ============================================================ */

    const name =
      formData?.name?.trim() || "";

    const email =
      formData?.email?.trim() || "";

    const phone =
      formData?.phone?.trim() || "";

    const serviceTitle =
      formData?.serviceTitle?.trim() || "";

    const subService =
      formData?.subService?.trim() || "";

    const message =
      formData?.message?.trim() || "";

    /* ============================================================
       EMAILJS TEMPLATE PARAMETERS
    ============================================================ */

    const templateParams = {
      to_email: ENQUIRY_EMAIL,

      from_name: name,
      from_email: email,
      phone: phone,

      service_title: serviceTitle,
      sub_service: subService,

      message: message,

      experience: formData?.experience || "",
      skills: formData?.skills || "",
      resume: formData?.resume || "",
    };

    /* ============================================================
       DEBUG
    ============================================================ */

    console.log(
      "EmailJS Enquiry Template Params:",
      templateParams
    );

    /* ============================================================
       SEND EMAIL
    ============================================================ */

    const response = await emailjs.send(
      ENQUIRY_SERVICE_ID,
      TEMPLATE_ID,
      templateParams,
      PUBLIC_KEY
    );

    /* ============================================================
       SUCCESS
    ============================================================ */

    console.log(
      "Enquiry email sent successfully:",
      response
    );

    return {
      success: true,
      response,
    };
  } catch (error) {
    /* ============================================================
       ERROR
    ============================================================ */

    console.error(
      "EmailJS Enquiry Error:",
      error
    );

    return {
      success: false,
      error,
    };
  }
};

/* ================================================================
   CONTACT SECTION
================================================================ */

const ContactSection = () => {
  /* ================================================================
     SERVICE DATA
  ================================================================= */

  const serviceData = {
    "Software Development": [
      "Custom Software Development",
      "Mobile App Development",
      "Web Development",
      "UI/UX Design",
      "CRM Solutions",
      "MLM Software",
    ],

    "API Services": [
      "WhatsApp API Services",
      "SMS OTP Services",
    ],

    "Legal Services": [
      "Business Registration",
      "Company Registration",
      "Taxation Services",
      "ISO Certification",
      "IPR Services",
    ],
  };

  /* ================================================================
     FORM STATE
  ================================================================= */

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    subService: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  /* ================================================================
     SUBMITTED STATE
  ================================================================= */

  const [submitted, setSubmitted] = useState(false);

  /* ================================================================
     AUTO HIDE SUCCESS NOTIFICATION
  ================================================================= */

  useEffect(() => {
    if (!submitted) return;

    const timer = setTimeout(() => {
      setSubmitted(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [submitted]);

  /* ================================================================
     FORM CHANGE
  ================================================================= */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,

      ...(name === "service"
        ? {
            subService: "",
          }
        : {}),
    }));
  };

  /* ================================================================
     PHONE CHANGE
  ================================================================= */

  const handlePhoneChange = (e) => {
    const value = e.target.value
      .replace(/\D/g, "")
      .slice(0, 10);

    setFormData((prev) => ({
      ...prev,
      phone: value,
    }));
  };

  /* ================================================================
     SUBMIT FORM
  ================================================================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    /* ============================================================
       VALIDATION
    ============================================================ */

    if (!formData.name.trim()) {
      alert("Please enter your full name.");
      return;
    }

    if (!formData.email.trim()) {
      alert("Please enter your email address.");
      return;
    }

    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(formData.email)) {
      alert("Please enter a valid email address.");
      return;
    }

    if (
      !formData.phone ||
      formData.phone.length !== 10
    ) {
      alert(
        "Please enter a valid 10-digit phone number."
      );
      return;
    }

    if (!formData.service) {
      alert("Please select a service.");
      return;
    }

    const selectedSubServices =
      serviceData[formData.service] || [];

    if (
      selectedSubServices.length > 0 &&
      !formData.subService
    ) {
      alert("Please select a sub-service.");
      return;
    }

    if (!formData.message.trim()) {
      alert("Please enter your message.");
      return;
    }

    /* ============================================================
       PREPARE ENQUIRY DATA
    ============================================================ */

    const enquiryData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      serviceTitle: formData.service,
      subService: formData.subService || "",
      message: formData.message,
    };

    setLoading(true);

    try {
      const result =
        await sendEnquiry(enquiryData);

      if (!result.success) {
        throw result.error;
      }

      /* ==========================================================
         SHOW SUCCESS TOAST
      ========================================================== */

      setSubmitted(true);

      /* ==========================================================
         RESET FORM
      ========================================================== */

      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        subService: "",
        message: "",
      });
    } catch (error) {
      console.error(
        "EmailJS Error:",
        error
      );

      alert(
        "Something went wrong while sending your message. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /* ================================================================
     CURRENT SUB-SERVICES
  ================================================================= */

  const currentSubServices = formData.service
    ? serviceData[formData.service] || []
    : [];

  const hasSubServices =
    currentSubServices.length > 0;

  /* ================================================================
     RETURN
  ================================================================= */

  return (
    <section
      className="
        relative
        w-full
        overflow-hidden
        font-saira
        text-[var(--text-primary)]
        bg-[var(--bg-primary)]
      "
    >
      {/* ==========================================================
          SUCCESS NOTIFICATION
      =========================================================== */}

      {submitted && (
        <div
          className="
            fixed
            right-4
            top-4
            z-[99999]
            w-[calc(100%-2rem)]
            max-w-[340px]
            overflow-hidden
            rounded-xl
            border
            p-3.5
            shadow-2xl
            backdrop-blur-xl
            sm:right-6
            sm:top-6
            sm:w-[340px]
          "
          style={{
            backgroundColor:
              "color-mix(in srgb, var(--card-bg) 96%, transparent)",

            borderColor:
              "color-mix(in srgb, var(--brand-primary) 30%, var(--border-primary))",

            boxShadow:
              "0 20px 55px color-mix(in srgb, var(--shadow-color) 70%, transparent)",
          }}
        >
          {/* ======================================================
              CONTENT
          ======================================================= */}

          <div className="flex items-start gap-3">
            {/* SUCCESS ICON */}

            <div
              className="
                flex
                h-9
                w-9
                shrink-0
                items-center
                justify-center
                rounded-full
              "
              style={{
                backgroundColor:
                  "color-mix(in srgb, var(--brand-primary) 12%, transparent)",

                color:
                  "var(--brand-primary)",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>

            {/* MESSAGE */}

            <div className="min-w-0 flex-1">
              <h3
                className="
                  text-sm
                  font-semibold
                  leading-5
                "
                style={{
                  color:
                    "var(--text-primary)",
                }}
              >
                Enquiry Submitted
              </h3>

              <p
                className="
                  mt-0.5
                  text-xs
                  leading-5
                "
                style={{
                  color:
                    "var(--text-muted)",
                }}
              >
                Thank you for reaching out.
                We'll contact you shortly.
              </p>
            </div>

            {/* CLOSE BUTTON */}

            <button
              type="button"
              onClick={() =>
                setSubmitted(false)
              }
              className="
                flex
                h-6
                w-6
                shrink-0
                items-center
                justify-center
                rounded-full
                text-lg
                leading-none
                transition-all
                hover:bg-black/5
                dark:hover:bg-white/5
              "
              style={{
                color:
                  "var(--text-muted)",
              }}
              aria-label="Close notification"
            >
              ×
            </button>
          </div>

          {/* ======================================================
              PROGRESS BAR
          ======================================================= */}

          <div
            className="
              absolute
              bottom-0
              left-0
              h-[2px]
              w-full
              origin-left
            "
            style={{
              backgroundColor:
                "var(--brand-primary)",

              animation:
                "submittedProgress 5s linear forwards",
            }}
          />
        </div>
      )}

      {/* ==========================================================
          NOISE BACKGROUND
      =========================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          inset-0
          z-[2]
          overflow-hidden
        "
      >
        <Noise
          patternSize={250}
          patternScaleX={2}
          patternScaleY={2}
          patternRefreshInterval={2}
          patternAlpha={15}
        />
      </div>

      {/* ==========================================================
          SUBTLE BACKGROUND GLOW
      =========================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          left-1/2
          top-0
          z-[1]
          h-[450px]
          w-[450px]
          -translate-x-1/2
          rounded-full
          blur-[130px]
        "
        style={{
          backgroundColor:
            "color-mix(in srgb, var(--brand-primary) 4%, transparent)",
        }}
      />

      {/* ==========================================================
          MAIN CONTAINER
      =========================================================== */}

      <div
        className="
          relative
          z-10
          mx-auto
          w-full
          max-w-[1550px]
          px-4
          py-8
          min-[380px]:px-5
          sm:px-6
          sm:py-10
          md:px-8
          md:py-12
          lg:px-10
          lg:py-14
          xl:px-12
          xl:py-16
          2xl:px-16
        "
      >
        {/* ========================================================
            MAIN CONTENT
        ========================================================= */}

        <div
          className="
            mx-auto
            grid
            w-full
            max-w-[1400px]
            grid-cols-1
            gap-8
            sm:gap-10
            md:gap-12
            lg:grid-cols-[0.95fr_1.05fr]
            lg:items-center
            lg:gap-10
            xl:gap-14
            2xl:gap-16
          "
        >
          {/* ======================================================
              LEFT CONTENT
          ======================================================= */}

          <div
            className="
              min-w-0
              w-full
              text-center
              lg:text-left
            "
          >
            {/* LABEL */}

            <div
              className="
                mb-2
                flex
                justify-center
                sm:mb-3
                lg:justify-start
              "
            >
              <span
                className="
                  inline-flex
                  items-center
                  text-[10px]
                  font-bold
                  uppercase
                  tracking-[0.22em]
                  min-[380px]:text-xs
                  sm:text-sm
                  sm:tracking-[0.25em]
                "
                style={{
                  color:
                    "var(--brand-primary)",
                }}
              >
                [ Get in Touch ]
              </span>
            </div>

            {/* HEADING */}

            <h1
              className="
                mx-auto
                w-full
                max-w-[780px]
                text-[26px]
                font-semibold
                leading-[1.12]
                tracking-tight
                min-[380px]:text-[28px]
                sm:text-3xl
                md:text-4xl
                lg:mx-0
                lg:text-[40px]
                lg:leading-[1.08]
                xl:text-[46px]
                2xl:text-[50px]
              "
              style={{
                color:
                  "var(--text-primary)",
              }}
            >
              <WavyText text="Have any Questions" />
              <br />
              <WavyText text="on Mind? Get in Touch" />
              <br />
              <WavyText text="For Market Experts." />
            </h1>

            {/* DESCRIPTION */}

            <p
              className="
                mx-auto
                mt-4
                w-full
                max-w-[620px]
                text-[13px]
                leading-6
                min-[380px]:text-sm
                sm:mt-5
                sm:text-base
                sm:leading-7
                lg:mx-0
                lg:mt-6
                lg:text-base
                lg:leading-7
                xl:max-w-[650px]
              "
              style={{
                color:
                  "var(--text-muted)",
              }}
            >
              Have an idea, project, or business challenge?
              Our experts are ready to understand your goals
              and deliver tailored digital solutions that drive
              real results. Let's start the conversation today.
            </p>

            {/* CONTACT INFORMATION */}

            <div
              className="
                mt-7
                border-t
                pt-5
                sm:mt-8
                sm:pt-6
                lg:mt-9
                lg:pt-6
                xl:mt-10
              "
              style={{
                borderColor:
                  "var(--border-primary)",
              }}
            >
              <div
                className="
                  grid
                  w-full
                  grid-cols-1
                  gap-6
                  min-[480px]:grid-cols-2
                  min-[480px]:gap-x-6
                  md:grid-cols-3
                  md:gap-6
                  lg:grid-cols-1
                  lg:gap-5
                  xl:grid-cols-3
                  xl:gap-7
                "
              >
                {/* CONTACT INFO */}

                <div className="min-w-0">
                  <h4
                    className="
                      mb-1.5
                      text-base
                      font-semibold
                      sm:mb-2
                      sm:text-lg
                    "
                    style={{
                      color:
                        "var(--text-primary)",
                    }}
                  >
                    Contact Info
                  </h4>

                  <p
                    className="
                      break-words
                      text-xs
                      leading-6
                      sm:text-sm
                      sm:leading-6
                    "
                    style={{
                      color:
                        "var(--text-muted)",
                    }}
                  >
                    +91 83288 92097
                    <br />
                    web.bocket@gmail.com
                  </p>
                </div>

                {/* ADDRESS */}

                <div className="min-w-0">
                  <h4
                    className="
                      mb-1.5
                      text-base
                      font-semibold
                      sm:mb-2
                      sm:text-lg
                    "
                    style={{
                      color:
                        "var(--text-primary)",
                    }}
                  >
                    Find Us
                  </h4>

                  <p
                    className="
                      text-xs
                      leading-6
                      sm:text-sm
                      sm:leading-6
                    "
                    style={{
                      color:
                        "var(--text-muted)",
                    }}
                  >
                    Kolathia Square,
                    <br />
                    Near Sri Sai Garden,
                    <br />
                    UCO Bank Top Floor,
                    <br />
                    Khandagiri,
                    <br />
                    Bhubaneswar - 751030
                  </p>
                </div>

                {/* OPENING HOURS */}

                <div className="min-w-0">
                  <h4
                    className="
                      mb-1.5
                      text-base
                      font-semibold
                      sm:mb-2
                      sm:text-lg
                    "
                    style={{
                      color:
                        "var(--text-primary)",
                    }}
                  >
                    Opening Hours
                  </h4>

                  <p
                    className="
                      text-xs
                      leading-6
                      sm:text-sm
                      sm:leading-6
                    "
                    style={{
                      color:
                        "var(--text-muted)",
                    }}
                  >
                    Mon - Sat{" "}
                    <span
                      className="font-medium"
                      style={{
                        color:
                          "var(--brand-primary)",
                      }}
                    >
                      (Open)
                    </span>
                    <br />
                    9 AM - 6 PM
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ======================================================
              RIGHT CONTACT FORM
          ======================================================= */}

          <div
            className="
              relative
              min-w-0
              w-full
              overflow-hidden
              rounded-2xl
              border
              p-4
              min-[380px]:p-5
              sm:rounded-3xl
              sm:p-6
              md:p-7
              lg:p-8
              xl:p-9
              2xl:p-10
            "
            style={{
              backgroundColor:
                "var(--card-bg)",

              borderColor:
                "var(--border-primary)",

              boxShadow:
                "0 18px 65px var(--shadow-color)",
            }}
          >
            {/* FORM GLOW - TOP RIGHT */}

            <div
              className="
                pointer-events-none
                absolute
                -right-20
                -top-20
                h-40
                w-40
                rounded-full
                blur-3xl
              "
              style={{
                backgroundColor:
                  "color-mix(in srgb, var(--brand-primary) 10%, transparent)",
              }}
            />

            {/* FORM GLOW - BOTTOM LEFT */}

            <div
              className="
                pointer-events-none
                absolute
                -bottom-20
                -left-20
                h-40
                w-40
                rounded-full
                blur-3xl
              "
              style={{
                backgroundColor:
                  "color-mix(in srgb, var(--brand-primary) 5%, transparent)",
              }}
            />

            {/* FORM CONTENT */}

            <div className="relative z-10 w-full">
              {/* FORM HEADING */}

              <h2
                className="
                  mb-4
                  text-center
                  text-xl
                  font-medium
                  leading-tight
                  min-[380px]:text-2xl
                  sm:mb-6
                  sm:text-3xl
                  lg:text-left
                  xl:text-3xl
                "
                style={{
                  color:
                    "var(--text-primary)",
                }}
              >
                <WavyText text="Drop Us a" />{" "}
                <span
                  style={{
                    color:
                      "var(--brand-primary)",
                  }}
                >
                  <WavyText text="Line." />
                </span>
              </h2>

              {/* CONTACT FORM */}

              <div className="contact-form-wrapper w-full min-w-0">
                <form
                  onSubmit={handleSubmit}
                  className="w-full"
                >
                  {/* NAME + EMAIL + PHONE + SERVICE */}

                  <div
                    className="
                      grid
                      grid-cols-1
                      gap-4
                      md:grid-cols-2
                    "
                  >
                    {/* FULL NAME */}

                    <div className="min-w-0">
                      <label
                        htmlFor="contact-name"
                        className="
                          mb-1.5
                          block
                          text-[11px]
                          font-semibold
                          uppercase
                          tracking-wide
                        "
                        style={{
                          color:
                            "var(--text-muted)",
                        }}
                      >
                        Full Name *
                      </label>

                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        autoComplete="name"
                        className="
                          w-full
                          rounded-lg
                          border
                          px-3.5
                          py-2.5
                          text-sm
                          outline-none
                          transition-all
                        "
                      />
                    </div>

                    {/* EMAIL */}

                    <div className="min-w-0">
                      <label
                        htmlFor="contact-email"
                        className="
                          mb-1.5
                          block
                          text-[11px]
                          font-semibold
                          uppercase
                          tracking-wide
                        "
                        style={{
                          color:
                            "var(--text-muted)",
                        }}
                      >
                        Email Address *
                      </label>

                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email"
                        autoComplete="email"
                        className="
                          w-full
                          rounded-lg
                          border
                          px-3.5
                          py-2.5
                          text-sm
                          outline-none
                          transition-all
                        "
                      />
                    </div>

                    {/* PHONE */}

                    <div className="min-w-0">
                      <label
                        htmlFor="contact-phone"
                        className="
                          mb-1.5
                          block
                          text-[11px]
                          font-semibold
                          uppercase
                          tracking-wide
                        "
                        style={{
                          color:
                            "var(--text-muted)",
                        }}
                      >
                        Phone Number *
                      </label>

                      <input
                        id="contact-phone"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        placeholder="Enter your 10-digit phone number"
                        inputMode="numeric"
                        maxLength={10}
                        autoComplete="tel"
                        className="
                          w-full
                          rounded-lg
                          border
                          px-3.5
                          py-2.5
                          text-sm
                          outline-none
                          transition-all
                        "
                      />
                    </div>

                    {/* SERVICE */}

                    <div className="min-w-0">
                      <label
                        htmlFor="contact-service"
                        className="
                          mb-1.5
                          block
                          text-[11px]
                          font-semibold
                          uppercase
                          tracking-wide
                        "
                        style={{
                          color:
                            "var(--text-muted)",
                        }}
                      >
                        Select Service *
                      </label>

                      <select
                        id="contact-service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="
                          w-full
                          cursor-pointer
                          rounded-lg
                          border
                          px-3.5
                          py-2.5
                          text-sm
                          outline-none
                          transition-all
                        "
                      >
                        <option value="">
                          Select a service
                        </option>

                        {Object.keys(serviceData).map(
                          (service) => (
                            <option
                              key={service}
                              value={service}
                            >
                              {service}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  </div>

                  {/* SUB-SERVICE */}

                  {hasSubServices && (
                    <div className="mt-4 w-full">
                      <label
                        htmlFor="contact-sub-service"
                        className="
                          mb-1.5
                          block
                          text-[11px]
                          font-semibold
                          uppercase
                          tracking-wide
                        "
                        style={{
                          color:
                            "var(--text-muted)",
                        }}
                      >
                        Select Sub-Service *
                      </label>

                      <select
                        id="contact-sub-service"
                        name="subService"
                        value={formData.subService}
                        onChange={handleChange}
                        className="
                          w-full
                          cursor-pointer
                          rounded-lg
                          border
                          px-3.5
                          py-2.5
                          text-sm
                          outline-none
                          transition-all
                        "
                      >
                        <option value="">
                          Select a sub-service
                        </option>

                        {currentSubServices.map(
                          (subService) => (
                            <option
                              key={subService}
                              value={subService}
                            >
                              {subService}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  )}

                  {/* MESSAGE */}

                  <div className="mt-4 w-full">
                    <label
                      htmlFor="contact-message"
                      className="
                        mb-1.5
                        block
                        text-[11px]
                        font-semibold
                        uppercase
                        tracking-wide
                      "
                      style={{
                        color:
                          "var(--text-muted)",
                      }}
                    >
                      Type Message
                    </label>

                    <textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your requirement..."
                      maxLength={1000}
                      rows={5}
                      className="
                        w-full
                        resize-none
                        rounded-lg
                        border
                        px-3.5
                        py-2.5
                        text-sm
                        outline-none
                        transition-all
                      "
                    />

                    <div
                      className="
                        mt-1
                        text-right
                        text-[10px]
                      "
                      style={{
                        color:
                          "var(--text-muted)",
                      }}
                    >
                      {formData.message.length}/1000
                    </div>
                  </div>

                  {/* SUBMIT BUTTON */}

                  <div className="mt-5">
                    <button
                      type="submit"
                      disabled={loading}
                      className="
                        flex
                        items-center
                        gap-2.5
                        rounded-full
                        px-4
                        py-2.5
                        text-sm
                        font-semibold
                        transition-all
                        disabled:cursor-not-allowed
                        disabled:opacity-60
                      "
                    >
                      {loading
                        ? "Sending..."
                        : "Send Message"}

                      {!loading && (
                        <span
                          className="
                            flex
                            h-7
                            w-7
                            items-center
                            justify-center
                            rounded-full
                            bg-black
                            text-white
                          "
                        >
                          ↗
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================
          RESPONSIVE / FORM STYLES
      ============================================================= */}

      <style jsx>{`
        section {
          contain: layout paint;
        }

        h1,
        h2,
        h3,
        h4,
        p {
          overflow-wrap: break-word;
          word-break: normal;
        }

        .contact-form-wrapper {
          width: 100%;
          min-width: 0;
        }

        .contact-form-wrapper input,
        .contact-form-wrapper textarea,
        .contact-form-wrapper select {
          max-width: 100%;
          min-width: 0;
          color: var(--text-primary);
          background-color: var(--bg-secondary);
          border-color: var(--border-primary);
        }

        .contact-form-wrapper input::placeholder,
        .contact-form-wrapper textarea::placeholder {
          color: var(--text-muted);
        }

        .contact-form-wrapper input:focus,
        .contact-form-wrapper textarea:focus,
        .contact-form-wrapper select:focus {
          outline: none;
          border-color: var(--brand-primary);
          box-shadow: 0 0 0 1px var(--brand-primary);
        }

        .contact-form-wrapper button[type="submit"] {
          background-color: var(--brand-primary);
          color: #ffffff;

          transition:
            background-color 0.3s ease,
            transform 0.3s ease,
            box-shadow 0.3s ease;
        }

        .contact-form-wrapper
          button[type="submit"]:hover:not(:disabled) {
          background-color: var(--brand-hover);
          transform: translateY(-1px);

          box-shadow:
            0 8px 22px
              color-mix(
                in srgb,
                var(--brand-primary) 25%,
                transparent
              );
        }

        .contact-form-wrapper
          button[type="submit"]:disabled {
          cursor: not-allowed;
        }

        .contact-form-wrapper select option {
          color: var(--text-primary);
          background-color: var(--bg-secondary);
        }

        /* ========================================================
           SUCCESS NOTIFICATION
        ========================================================= */

        @keyframes submittedProgress {
          from {
            transform: scaleX(1);
          }

          to {
            transform: scaleX(0);
          }
        }

        @media (max-width: 379px) {
          section {
            overflow-x: hidden;
          }

          .contact-form-wrapper {
            width: 100%;
          }
        }

        @media (min-width: 380px) and (max-width: 639px) {
          section {
            overflow-x: hidden;
          }
        }

        @media (min-width: 640px) and (max-width: 1023px) {
          section {
            overflow-x: hidden;
          }
        }

        @media (min-width: 1024px) {
          section {
            overflow-x: hidden;
          }
        }

        @media (min-width: 1280px) {
          .contact-form-wrapper {
            width: 100%;
          }
        }
      `}</style>
    </section>
  );
};

export default ContactSection;